import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, Users, Briefcase, FileText, Wallet, Percent, 
  Search, Plus, Trash2, Edit, X, 
  DollarSign, Sparkles, MessageSquare, Copy, ChevronRight, AlertCircle, User,
  // Removed unused 'Phone' import
  Save, Upload, Truck, Lock, Zap, CheckCircle
} from 'lucide-react';

// --- KONFIGURASI API (DOMAIN KARYA) ---
const API_URL = "https://karya.mamiberlianagency.com/api.php"; 

// --- UTILITIES ---
function formatRupiah(number) {
  if (isNaN(number) || number === null || number === undefined) return 'Rp 0';
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(number);
}

function formatNumberInput(value) { 
  if (!value && value !== 0) return ''; 
  return new Intl.NumberFormat('id-ID').format(value); 
}

function parseNumberInput(valueString) { 
  if (!valueString) return 0; 
  return parseInt(String(valueString).replace(/\./g, '')) || 0; 
}

function calculateAge(dobString) {
  if (!dobString) return 0;
  const today = new Date();
  const birthDate = new Date(dobString);
  let age = today.getFullYear() - birthDate.getFullYear();
  if (today.getMonth() < birthDate.getMonth() || (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate())) age--;
  return age;
}

function calculateDate(startDate, monthsToAdd) {
  if (!startDate) return '';
  const date = new Date(startDate);
  date.setMonth(date.getMonth() + monthsToAdd);
  return date.toISOString().split('T')[0];
}

function getWarrantyMonths(duration) {
  if (duration === 4) return 2; if (duration === 6) return 3; if (duration === 12) return 4; return 0;
}

function generateRegNo(lastId, type, year, jobCategory = 'ART') {
  if (type === 'employer') { const num = String(lastId + 1).padStart(3, '0'); return `MMBA-${num}-${year}`; }
  const prefix = 'PMBA';
  let suffix = 'ART';
  if (jobCategory === 'Suster Bayi') suffix = 'SUSBY';
  else if (jobCategory === 'Suster Balita') suffix = 'SUSBL';
  else if (jobCategory === 'Suster Lansia') suffix = 'SUSL';
  const num = String(lastId + 1).padStart(4, '0'); 
  return `${prefix}-${num}-${suffix}`;
}

function isValidKTP(ktp) { if (!ktp) return false; return /^\d{16}$/.test(ktp); }

// --- SUB-COMPONENTS ---

function Sidebar({ activeTab, setActiveTab, isOpen, setIsOpen }) {
  const menu = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'workers', label: 'Data Pekerja', icon: Users },
    { id: 'employers', label: 'Data Majikan', icon: Briefcase },
    { id: 'loans', label: 'Talangan Travel', icon: Wallet },
    { id: 'contracts', label: 'Kontrak & Arsip', icon: FileText },
    { id: 'evaluations', label: 'Gaji Evaluasi', icon: Percent },
  ];

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden" onClick={() => setIsOpen(false)} />}
      <aside className={`fixed inset-y-0 left-0 z-30 w-64 bg-slate-900 text-white transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0 flex flex-col shadow-xl`}>
        <div className="h-20 flex items-center px-6 border-b border-slate-700 bg-slate-900">
          <div>
            <h1 className="text-xl font-bold text-yellow-400 tracking-wider">MAMI BERLIAN</h1>
            <p className="text-[10px] text-slate-400 uppercase tracking-widest">Management System</p>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-6 px-3 space-y-1 bg-slate-900">
          {menu.map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setIsOpen(false); }}
              className={`w-full flex items-center px-4 py-3 rounded-lg transition-all duration-200 group ${
                activeTab === item.id 
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50' 
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <item.icon size={20} className={`mr-3 ${activeTab === item.id ? 'text-white' : 'text-slate-400 group-hover:text-white'}`} />
              <span className="font-medium text-sm">{item.label}</span>
              {activeTab === item.id && <ChevronRight size={16} className="ml-auto" />}
            </button>
          ))}
        </nav>
      </aside>
    </>
  );
}

function StatCard({ title, value, icon: Icon, color, subtext, onClick }) {
  return (
    <div 
      onClick={onClick}
      className={`bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group ${onClick ? 'cursor-pointer' : ''}`}
    >
      <div className={`absolute -right-4 -top-4 opacity-10 group-hover:scale-110 transition-transform duration-500 ${color.replace('bg-', 'text-')}`}>
        <Icon size={100} />
      </div>
      <div className="flex items-center gap-4 mb-2">
        <div className={`p-3 rounded-xl ${color} text-white shadow-lg`}>
          <Icon size={24} />
        </div>
        <span className="text-slate-500 font-medium text-sm">{title}</span>
      </div>
      <h3 className="text-2xl font-bold text-slate-800 pl-1">{value}</h3>
      {subtext && <p className="text-xs text-slate-400 mt-2 pl-1">{subtext}</p>}
    </div>
  );
}

function StatusBadge({ status, type }) {
  let styles = "bg-gray-100 text-gray-600";
  if (type === 'worker') {
    if (status === 'Bekerja') styles = "bg-blue-100 text-blue-700 border border-blue-200";
    if (status === 'Tersedia') styles = "bg-emerald-100 text-emerald-700 border border-emerald-200";
  } else if (type === 'payment') {
    if (status === 'Lunas') styles = "bg-emerald-100 text-emerald-700 border border-emerald-200";
    if (status === 'Belum Lunas' || status === 'Belum Bayar') styles = "bg-red-100 text-red-700 border border-red-200";
  } else if (type === 'contractType') {
    if (status === 'Baru') styles = "bg-blue-100 text-blue-700 border border-blue-200";
    if (status === 'Adendum') styles = "bg-purple-100 text-purple-700 border border-purple-200";
  }
  
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${styles}`}>
      {status}
    </span>
  );
}

function DataTable({ columns, data, renderRow, onAdd, onEdit, onDelete, searchTerm, setSearchTerm }) {
  return (
    <div className="space-y-4 animate-fadeIn">
      <div className="flex flex-col md:flex-row justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-slate-100 gap-4">
         <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-2.5 text-slate-400" size={16}/>
            <input 
              className="pl-9 pr-4 py-2 border rounded-lg w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" 
              placeholder="Cari data..." 
              value={searchTerm} 
              onChange={e=>setSearchTerm(e.target.value)}
            />
         </div>
         {onAdd && (
           <button onClick={onAdd} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 shadow-sm transition-all w-full md:w-auto justify-center">
             <Plus size={16}/> Tambah
           </button>
         )}
      </div>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-slate-100">
         <div className="overflow-x-auto">
           <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 border-b">
                <tr>
                  {columns.map((c,i) => <th key={i} className="p-4 font-semibold text-slate-700">{c}</th>)}
                  <th className="p-4 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                 {(Array.isArray(data) ? data : []).filter(d => JSON.stringify(d).toLowerCase().includes(searchTerm.toLowerCase())).map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                       {renderRow(item)}
                       <td className="p-4 flex justify-center gap-2">
                          {onEdit && <button onClick={()=>onEdit(item)} className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors"><Edit size={14}/></button>}
                          {onDelete && <button onClick={()=>onDelete(item.id)} className="p-2 text-red-600 bg-red-50 hover:bg-red-100 rounded transition-colors"><Trash2 size={14}/></button>}
                       </td>
                    </tr>
                 ))}
                 {(!data || data.length === 0) && (
                   <tr>
                     <td colSpan={columns.length + 1} className="p-8 text-center text-slate-400">Belum ada data</td>
                   </tr>
                 )}
              </tbody>
           </table>
         </div>
      </div>
    </div>
  );
}

// --- MAIN APP COMPONENT ---
export default function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({ type: '', data: null });
  const [searchTerm, setSearchTerm] = useState('');
  
  // AI Loading State
  const [isAILoading, setIsAILoading] = useState(false);
  const [generatedMessage, setGeneratedMessage] = useState('');

  // Data State
  const [workers, setWorkers] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [loans, setLoans] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null); 

  // Form State
  const [formData, setFormData] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);

  // FETCH DATA
  const fetchData = async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const endpoints = ['get_workers', 'get_employers', 'get_loans', 'get_contracts', 'get_evaluations'];
      const responses = await Promise.all(
        endpoints.map(action => fetch(`${API_URL}?action=${action}`))
      );

      const parseResponse = async (res, index) => {
        if (!res.ok) {
          throw new Error(`Server Error (${res.status}) on ${endpoints[index]}`);
        }
        const text = await res.text();
        try {
          return JSON.parse(text);
        } catch (e) {
          console.warn(`Invalid JSON from ${endpoints[index]}:`, text.substring(0, 100));
          return [];
        }
      };

      const [dataW, dataE, dataL, dataC, dataEv] = await Promise.all(
        responses.map((res, i) => parseResponse(res, i))
      );

      setWorkers(Array.isArray(dataW) ? dataW : []);
      setEmployers(Array.isArray(dataE) ? dataE : []);
      setLoans(Array.isArray(dataL) ? dataL : []);
      setContracts(Array.isArray(dataC) ? dataC : []);
      setEvaluations(Array.isArray(dataEv) ? dataEv : []);

    } catch (error) { 
      console.error("Fetch Data Error:", error);
      setFetchError(error.message);
    } finally { 
      setIsLoading(false); 
    }
  };

  useEffect(() => { fetchData(); }, []);

  // --- AI HANDLERS ---
  const handleGenerateProfile = async () => {
    setIsAILoading(true);
    setTimeout(() => {
        setFormData(prev => ({ 
            ...prev, 
            profileSummary: `Profesional ${formData.jobCategory || 'Pekerja'} berpengalaman. ${formData.name || 'Nama'} adalah pribadi yang rajin, jujur, dan bertanggung jawab. Siap bekerja dengan dedikasi tinggi.` 
        }));
        setIsAILoading(false);
    }, 1500);
  };

  const handleGenerateWAMessage = async () => {
    const employer = employers.find(e => e.id === parseInt(formData.employerId));
    const worker = workers.find(w => w.id === parseInt(formData.workerId));
    
    if (!employer || !worker) {
        alert("Pilih Majikan dan Pekerja terlebih dahulu.");
        return;
    }

    const totalBill = (parseInt(formData.adminFee) || 0) + (parseInt(formData.deliveryFee) || 0) + (parseInt(formData.holdFee) || 0) + (parseInt(formData.extraCharge) || 0);

    setIsAILoading(true);
    setTimeout(() => {
        const msg = `Yth. Ibu/Bapak ${employer.name},\n\nBerikut konfirmasi kontrak untuk:\nPekerja: ${worker.name} (${worker.jobCategory})\nMulai: ${formData.startDate}\nDurasi: ${formData.duration} Bulan\n\nTotal Biaya: ${formatRupiah(totalBill)}\n\nMohon ditransfer sesuai nominal. Terima kasih atas kepercayaannya pada Mami Berlian Agency.`;
        setGeneratedMessage(msg);
        setIsAILoading(false);
    }, 1500);
  };

  // HANDLERS
  const handleOpenModal = (type, data = null) => {
    setModalConfig({ type, data });
    setSelectedFile(null);
    setGeneratedMessage('');
    if (data) {
      setFormData({ ...data });
    } else {
      const year = new Date().getFullYear();
      let defaults = {};
      const nextWorkerId = (workers.length > 0 ? Math.max(...workers.map(w => parseInt(w.id) || 0)) : 0) + 500;
      const nextEmpId = (employers.length > 0 ? Math.max(...employers.map(e => parseInt(e.id) || 0)) : 0) + 600;

      if (type === 'worker') defaults = { regNo: generateRegNo(nextWorkerId, 'worker', null, 'ART'), status: 'Tersedia', education: 'SMP', religion: 'Islam', jobCategory: 'ART', name: '' };
      else if (type === 'employer') defaults = { regNo: generateRegNo(nextEmpId, 'employer', year) };
      else if (type === 'contract') defaults = { contractType: 'Baru', date: new Date().toISOString().split('T')[0], startDate: new Date().toISOString().split('T')[0], duration: 12, adminFee: 0, deliveryFee: 0, holdFee: 0, extraCharge: 0, salary: 0, hasEvaluationSalary: true };
      else if (type === 'loan') defaults = { date: new Date().toISOString().split('T')[0], status: 'Belum Lunas', amount: 0 };
      setFormData(defaults);
    }
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let val = type === 'checkbox' ? checked : value;
    
    if (name === 'dob') {
        setFormData(prev => ({ ...prev, [name]: val, age: calculateAge(val) }));
    } else if (name === 'name') {
        setFormData(prev => ({ ...prev, [name]: val.toUpperCase() }));
    } else if (name === 'jobCategory' && !modalConfig.data) {
        const nextId = (workers.length > 0 ? Math.max(...workers.map(w => parseInt(w.id) || 0)) : 0) + 500;
        setFormData(prev => ({ ...prev, [name]: val, regNo: generateRegNo(nextId, 'worker', null, val) }));
    } else {
        setFormData(prev => ({ ...prev, [name]: val }));
    }
  };

  const handleCurrencyChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: parseNumberInput(e.target.value) }));
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile({ file, fieldName });
      setFormData(prev => ({ ...prev, [fieldName]: URL.createObjectURL(file) })); 
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((modalConfig.type === 'worker' || modalConfig.type === 'employer') && formData.ktp && !isValidKTP(formData.ktp)) {
      alert("KTP harus 16 digit!");
      return;
    }

    const payload = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && formData[key] !== undefined) {
        payload.append(key, formData[key]);
      }
    });
    
    // Recalculate warranty on submit
    if (modalConfig.type === 'contract') {
        const wMonths = getWarrantyMonths(parseInt(formData.duration));
        const wDate = calculateDate(formData.startDate, wMonths);
        payload.append('warrantyDate', wDate);
    }

    if (selectedFile) payload.append('photoFile', selectedFile.file);

    let action = `save_${modalConfig.type}`;
    if (modalConfig.type === 'pay_eval') action = 'save_evaluation';

    try {
        const response = await fetch(`${API_URL}?action=${action}`, { method: 'POST', body: payload });
        const result = await response.json();
        if (result.status === 'success') {
            alert("Berhasil!");
            fetchData();
            setIsModalOpen(false);
        } else {
            alert("Gagal: " + (result.error || "Unknown Error"));
        }
    } catch (err) { alert("Koneksi Error: " + err.message); }
  };

  const handleDelete = async (type, id) => {
    if(!window.confirm("Hapus?")) return;
    try {
      await fetch(`${API_URL}?action=delete_${type}`, { method: 'POST', body: JSON.stringify({ id }) });
      fetchData();
    } catch(e) { alert("Gagal menghapus"); }
  };

  const renderDashboard = () => {
    const safeContracts = Array.isArray(contracts) ? contracts : [];
    const safeLoans = Array.isArray(loans) ? loans : [];
    const safeEvals = Array.isArray(evaluations) ? evaluations : [];
    const safeWorkers = Array.isArray(workers) ? workers : [];

    const totalAdminFee = safeContracts.reduce((acc, c) => acc + (parseInt(c.adminFee) || 0), 0);
    const totalExtraRevenue = safeContracts.reduce((acc, c) => acc + (parseInt(c.deliveryFee)||0) + (parseInt(c.holdFee)||0) + (parseInt(c.extraCharge)||0), 0);
    const pendingLoans = safeLoans.filter(l => l.status === 'Belum Lunas').reduce((acc, l) => acc + (parseInt(l.amount) || 0), 0);
    const pendingEvals = safeEvals.filter(e => e.status === 'Belum Bayar').reduce((acc, e) => acc + (parseInt(e.amount) || 0), 0);

    return (
      <div className="space-y-6 animate-fadeIn">
        {fetchError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative flex items-center gap-2">
            <AlertCircle size={20} />
            <div>
              <strong className="font-bold">Gagal Menghubungkan Database!</strong>
              <span className="block sm:inline"> {fetchError}. Mohon cek file api.php di hosting Anda.</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard title="Total Admin Fee" value={formatRupiah(totalAdminFee)} icon={DollarSign} color="bg-blue-600" subtext={`+ ${formatRupiah(totalExtraRevenue)} Biaya Lainnya`} onClick={() => {}} />
          <StatCard title="Piutang Travel" value={formatRupiah(pendingLoans)} icon={Wallet} color="bg-rose-500" subtext="Talangan Belum Lunas" onClick={() => setActiveTab('loans')} />
          <StatCard title="Potensi Gaji Eval" value={formatRupiah(pendingEvals)} icon={Percent} color="bg-amber-500" subtext="Tagihan 50% Gaji ke Depan" onClick={() => setActiveTab('evaluations')} />
          <StatCard title="Pekerja Ready" value={safeWorkers.filter(w => w.status === 'Tersedia').length} icon={Users} color="bg-emerald-500" subtext={`Dari Total ${safeWorkers.length} Pekerja`} onClick={() => setActiveTab('workers')} />
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><FileText size={18}/> Kontrak Terbaru</h3>
            <div className="space-y-4">
              {safeContracts.slice(0, 5).map(c => (
                <div key={c.id} className="flex justify-between items-center p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <div>
                    <p className="font-bold text-slate-800">{c.workerName}</p>
                    <p className="text-xs text-slate-500">{c.employerName}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-blue-600">{formatRupiah(c.adminFee)}</p>
                    <p className="text-xs text-slate-400">{c.contractDate}</p>
                  </div>
                </div>
              ))}
              {safeContracts.length === 0 && <p className="text-slate-400 text-sm">Belum ada kontrak.</p>}
            </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen bg-slate-50 font-sans text-slate-900">
       <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} isOpen={sidebarOpen} setIsOpen={setSidebarOpen}/>
       <main className="flex-1 flex flex-col h-screen overflow-hidden">
          {isLoading && <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}
          <div className="flex-1 overflow-auto p-4 md:p-8">
             {activeTab === 'dashboard' && renderDashboard()}
             
             {activeTab === 'workers' && <DataTable columns={['Nama/Reg', 'Usia/Asal', 'Kontak', 'Fisik', 'Status']} data={workers} searchTerm={searchTerm} setSearchTerm={setSearchTerm} onAdd={()=>handleOpenModal('worker')} onEdit={(i)=>handleOpenModal('worker',i)} onDelete={(id)=>handleDelete('worker',id)} renderRow={(w)=><React.Fragment><td className="p-4"><div className="flex items-center gap-3">{w.photoUrl ? <img src={w.photoUrl} alt="Avatar" className="w-10 h-10 rounded-full object-cover"/> : <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center"><User size={20}/></div>}<div><div className="font-bold">{w.name}</div><div className="text-xs text-blue-600">{w.regNo}</div></div></div></td><td className="p-4">{w.age} Thn / {w.origin}</td><td className="p-4">{w.phone}<br/><span className="text-xs text-slate-400">Darurat: {w.emergencyContact}</span></td><td className="p-4">{w.height}cm/{w.weight}kg</td><td className="p-4"><StatusBadge status={w.status} type="worker"/></td></React.Fragment>}/>}
             
             {activeTab === 'employers' && <DataTable columns={['Nama', 'Alamat', 'Kontak', 'Status']} data={employers} searchTerm={searchTerm} setSearchTerm={setSearchTerm} onAdd={()=>handleOpenModal('employer')} onEdit={(i)=>handleOpenModal('employer',i)} renderRow={(e)=><React.Fragment><td className="p-4">{e.name}</td><td className="p-4">{e.address}</td><td className="p-4">{e.phone}</td><td className="p-4">{e.status}</td></React.Fragment>}/>}
             
             {activeTab === 'contracts' && <DataTable columns={['No Surat', 'Pekerja', 'Majikan', 'Fee', 'Gaji']} data={contracts} searchTerm={searchTerm} setSearchTerm={setSearchTerm} onAdd={()=>handleOpenModal('contract')} onEdit={(i)=>handleOpenModal('contract',i)} onDelete={(id)=>handleDelete('contract',id)} renderRow={(c)=><React.Fragment><td className="p-4">{c.letterNo}</td><td className="p-4">{c.workerName}</td><td className="p-4">{c.employerName}</td><td className="p-4">{formatRupiah(c.adminFee)}</td><td className="p-4">{formatRupiah(c.salary)}</td></React.Fragment>}/>}
             
             {activeTab === 'loans' && <DataTable columns={['Tanggal', 'Pekerja', 'Nominal', 'Status']} data={loans} searchTerm={searchTerm} setSearchTerm={setSearchTerm} onAdd={()=>handleOpenModal('loan')} onEdit={(i)=>handleOpenModal('loan',i)} onDelete={(id)=>handleDelete('loan',id)} renderRow={(l)=><React.Fragment><td className="p-4">{l.date}</td><td className="p-4">{l.workerName}</td><td className="p-4">{formatRupiah(l.amount)}</td><td className="p-4">{l.status}</td></React.Fragment>}/>}
             
             {activeTab === 'evaluations' && <DataTable columns={['Pekerja', 'Majikan', 'Tagihan', 'Status']} data={evaluations} searchTerm={searchTerm} setSearchTerm={setSearchTerm} renderRow={(ev)=><React.Fragment><td className="p-4">{ev.workerName}</td><td className="p-4">{ev.employerName}</td><td className="p-4">{formatRupiah(ev.amount)}</td><td className="p-4 flex gap-2"><StatusBadge status={ev.status} type="payment"/>{ev.status==='Belum Bayar' && <button onClick={()=>handleOpenModal('pay_eval', ev)} className="text-xs bg-blue-600 text-white px-2 py-1 rounded">Bayar</button>}</td></React.Fragment>}/>}
          </div>
       </main>

       {isModalOpen && (
         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-fadeIn">
               <div className="p-6 border-b flex justify-between bg-white sticky top-0 z-10">
                  <h2 className="font-bold text-xl uppercase text-slate-800">
                    {modalConfig.type === 'pay_eval' ? 'Pembayaran' : `Form ${modalConfig.type}`}
                  </h2>
                  <button onClick={()=>setIsModalOpen(false)} className="p-1 hover:bg-slate-100 rounded-full"><X/></button>
               </div>
               <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  {modalConfig.type === 'worker' && (
                     <div className="grid md:grid-cols-2 gap-4">
                        <div className="md:col-span-2 flex justify-center mb-4"><div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center relative border border-slate-300 overflow-hidden"><img src={formData.photoUrl} className="w-full h-full object-cover" alt="Profile"/><input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e)=>handleFileChange(e,'photoUrl')}/></div></div>
                        <input name="regNo" value={formData.regNo} readOnly className="border p-2 rounded bg-slate-100 w-full"/>
                        <input name="name" value={formData.name || ''} onChange={handleInputChange} placeholder="NAMA LENGKAP" className="border p-2 rounded uppercase w-full" required/>
                        <select name="jobCategory" value={formData.jobCategory} onChange={handleInputChange} className="border p-2 rounded w-full"><option value="ART">ART</option><option value="Suster Bayi">Suster Bayi</option><option value="Suster Balita">Suster Balita</option><option value="Suster Lansia">Suster Lansia</option></select>
                        <input name="birthPlace" value={formData.birthPlace || ''} onChange={handleInputChange} placeholder="Tempat Lahir" className="border p-2 rounded w-full"/>
                        <input type="date" name="dob" value={formData.dob || ''} onChange={handleInputChange} className="border p-2 rounded w-full"/>
                        <input name="ktp" value={formData.ktp || ''} onChange={handleInputChange} placeholder="No KTP (16 Digit)" className="border p-2 rounded w-full"/>
                        {formData.ktp && !isValidKTP(formData.ktp) && <p className="text-red-500 text-xs">KTP Tidak Valid</p>}
                        <input name="phone" value={formData.phone || ''} onChange={handleInputChange} placeholder="No HP" className="border p-2 rounded w-full"/>
                        <input name="origin" value={formData.origin || ''} onChange={handleInputChange} placeholder="Asal Domisili" className="border p-2 rounded w-full"/>
                        
                        <div className="md:col-span-2 bg-indigo-50 p-4 rounded-lg border border-indigo-200 mt-4">
                           <div className="flex justify-between items-center mb-2">
                              <label className="text-xs font-bold text-indigo-800 flex items-center gap-2"><Sparkles size={14}/> Profil Singkat (Promosi)</label>
                              <button 
                                type="button" 
                                onClick={handleGenerateProfile} 
                                disabled={isAILoading}
                                className="text-xs bg-indigo-600 text-white px-3 py-1 rounded-full hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1 transition-all"
                              >
                                {isAILoading ? 'Memproses...' : '✨ Buat Otomatis dengan AI'}
                              </button>
                           </div>
                           <textarea 
                              name="profileSummary" 
                              rows="3" 
                              placeholder="Deskripsi profil pekerja untuk dikirim ke majikan..." 
                              value={formData.profileSummary || ''} 
                              onChange={handleInputChange} 
                              className="w-full border p-2 rounded border-indigo-200 focus:ring-indigo-500"
                           />
                        </div>

                        {!modalConfig.data && (
                            <div className="md:col-span-2 border-t pt-2">
                                <label className="flex gap-2 font-bold text-sm"><input type="checkbox" name="hasTravelLoan" checked={formData.hasTravelLoan} onChange={handleInputChange}/> Ada Talangan Travel?</label>
                                {formData.hasTravelLoan && <div className="grid grid-cols-2 gap-2 mt-2"><input name="travelLoanAmount" value={formatNumberInput(formData.travelLoanAmount)} onChange={handleCurrencyChange} placeholder="Nominal" className="border p-2 rounded"/><input name="travelLoanNote" value={formData.travelLoanNote||''} onChange={handleInputChange} placeholder="Keterangan" className="border p-2 rounded"/></div>}
                            </div>
                        )}
                     </div>
                  )}
                  {modalConfig.type === 'employer' && (
                      <div className="grid gap-4">
                          <input name="name" value={formData.name||''} onChange={handleInputChange} placeholder="Nama Majikan" className="border p-2 rounded w-full" required/>
                          <input name="ktp" value={formData.ktp||''} onChange={handleInputChange} placeholder="No KTP" className="border p-2 rounded w-full"/>
                          {formData.ktp && !isValidKTP(formData.ktp) && <p className="text-red-500 text-xs">KTP Tidak Valid</p>}
                          <input name="phone" value={formData.phone||''} onChange={handleInputChange} placeholder="No HP" className="border p-2 rounded w-full"/>
                          <div className="border p-2 rounded relative flex items-center gap-2"><Upload size={16} className="text-slate-400"/><p className="text-xs text-gray-500 mb-1">Upload KTP</p><input type="file" onChange={(e)=>handleFileChange(e,'ktpUrl')}/></div>
                          {formData.ktpUrlName && <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle size={12}/> {formData.ktpUrlName}</p>}
                      </div>
                  )}
                  {modalConfig.type === 'contract' && (
                      <div className="grid md:grid-cols-2 gap-4">
                          <select name="contractType" value={formData.contractType} onChange={handleInputChange} className="border p-2 rounded w-full"><option value="Baru">Baru</option><option value="Adendum">Adendum</option></select>
                          <input name="letterNo" value={formData.letterNo||''} onChange={handleInputChange} className="border p-2 rounded w-full"/>
                          <input type="date" name="startDate" value={formData.startDate} onChange={handleInputChange} className="border p-2 rounded w-full"/>
                          <select name="duration" value={formData.duration} onChange={handleInputChange} className="border p-2 rounded w-full"><option value="4">4 Bulan</option><option value="6">6 Bulan</option><option value="12">12 Bulan</option></select>
                          <select name="workerId" value={formData.workerId||''} onChange={handleInputChange} className="border p-2 rounded w-full"><option value="">Pilih Pekerja</option>{workers.filter(w=>w.status==='Tersedia').map(w=><option key={w.id} value={w.id}>{w.name}</option>)}</select>
                          <select name="employerId" value={formData.employerId||''} onChange={handleInputChange} className="border p-2 rounded w-full"><option value="">Pilih Majikan</option>{employers.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}</select>
                          <div className="md:col-span-2 bg-slate-50 p-3 rounded grid grid-cols-2 gap-2 border border-slate-200">
                             <div className="flex items-center gap-1"><DollarSign size={14} className="text-slate-400"/><input name="adminFee" value={formatNumberInput(formData.adminFee)} onChange={handleCurrencyChange} placeholder="Admin Fee" className="border p-1 rounded w-full text-sm"/></div>
                             <div className="flex items-center gap-1"><Truck size={14} className="text-slate-400"/><input name="deliveryFee" value={formatNumberInput(formData.deliveryFee)} onChange={handleCurrencyChange} placeholder="Ongkir" className="border p-1 rounded w-full text-sm"/></div>
                             <div className="flex items-center gap-1"><Lock size={14} className="text-slate-400"/><input name="holdFee" value={formatNumberInput(formData.holdFee)} onChange={handleCurrencyChange} placeholder="Keep Awal" className="border p-1 rounded w-full text-sm"/></div>
                             <div className="flex items-center gap-1"><Zap size={14} className="text-slate-400"/><input name="extraCharge" value={formatNumberInput(formData.extraCharge)} onChange={handleCurrencyChange} placeholder="Extra" className="border p-1 rounded w-full text-sm"/></div>
                          </div>
                          <input name="salary" value={formatNumberInput(formData.salary)} onChange={handleCurrencyChange} placeholder="Gaji" className="border p-2 rounded w-full"/>
                          <div className="md:col-span-2"><label className="flex gap-2 text-sm"><input type="checkbox" name="hasEvaluationSalary" checked={formData.hasEvaluationSalary} onChange={handleInputChange}/> Ada Gaji Evaluasi (50%)?</label></div>
                          
                          {formData.workerId && formData.employerId && (
                            <div className="md:col-span-2 bg-emerald-50 p-4 rounded-lg border border-emerald-200 mt-2">
                               <div className="flex justify-between items-center mb-2">
                                  <label className="text-xs font-bold text-emerald-800 flex items-center gap-2"><MessageSquare size={14}/> Draft Pesan WhatsApp</label>
                                  <button 
                                    type="button" 
                                    onClick={handleGenerateWAMessage} 
                                    className="text-xs bg-emerald-600 text-white px-3 py-1 rounded-full hover:bg-emerald-700 flex items-center gap-1 transition-all"
                                  >
                                    ✨ Draft WA Otomatis
                                  </button>
                               </div>
                               {generatedMessage && (
                                 <div className="bg-white p-3 rounded border border-emerald-100 text-xs text-slate-600 relative">
                                    <pre className="whitespace-pre-wrap font-sans">{generatedMessage}</pre>
                                    <button 
                                      type="button" 
                                      onClick={() => navigator.clipboard.writeText(generatedMessage)} 
                                      className="absolute top-2 right-2 text-slate-400 hover:text-emerald-600" 
                                      title="Copy Text"
                                    >
                                      <Copy size={16}/>
                                    </button>
                                 </div>
                               )}
                            </div>
                          )}
                      </div>
                  )}
                  {modalConfig.type === 'loan' && (
                      <div className="grid gap-4">
                          <select name="workerId" value={formData.workerId||''} onChange={handleInputChange} className="border p-2 rounded w-full"><option value="">Pilih Pekerja</option>{workers.map(w=><option key={w.id} value={w.id}>{w.name}</option>)}</select>
                          <input name="amount" value={formatNumberInput(formData.amount)} onChange={handleCurrencyChange} placeholder="Nominal" className="border p-2 rounded w-full"/>
                          <select name="status" value={formData.status} onChange={handleInputChange} className="border p-2 rounded w-full"><option value="Belum Lunas">Belum Lunas</option><option value="Lunas">Lunas</option></select>
                          {formData.status === 'Lunas' && <input type="date" name="paidDate" value={formData.paidDate||''} onChange={handleInputChange} className="border p-2 rounded w-full"/>}
                      </div>
                  )}
                  {modalConfig.type === 'pay_eval' && (
                      <div className="text-center">
                          <h3 className="text-lg font-bold mb-2">Bayar Gaji Evaluasi</h3>
                          <p className="mb-4 text-2xl font-bold text-amber-600">{formatRupiah(formData.amount)}</p>
                          <input type="date" name="paidDate" value={formData.paidDate||''} onChange={handleInputChange} className="border p-2 rounded w-full"/>
                      </div>
                  )}

                  <div className="flex justify-end gap-2 pt-4 border-t mt-4">
                     <button type="button" onClick={()=>setIsModalOpen(false)} className="px-4 py-2 border rounded text-slate-600 hover:bg-slate-50 transition-colors">Batal</button>
                     <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors shadow-sm"><Save size={16} className="inline mr-2"/>Simpan</button>
                  </div>
               </form>
            </div>
         </div>
       )}
    </div>
  );
}